import { Box, Chip, Text, citiboxColors } from '@citibox-panel/ui';

// The overline's own line (dense, so line-height 1). The label and the step counter share it, and
// the counter — taller than the type by design — is centred on it, so it overhangs the line by a
// couple of pixels instead of pushing the label down.
const LOCK_CODE_LABEL_LINE_PX = 12;

// Height of the label block above the code box — the label's line plus the gap down to the box.
// Published, and enforced below as a real height rather than left to fall out of the type metrics,
// because the carril's divider has to start exactly where the boxes start: it skips this block and
// stretches over what is left, which is the box. Change it and the label's breathing room and the
// divider's top move together, which is the point of the constant — the gap was 8px and reads
// tight under an all-caps label, so it is 12px.
export const LOCK_CODE_LABEL_BLOCK_PX = 24;

export interface LockCodeSlotProps {
  label: string;
  // `null` when the slot falls outside the window the read returned — at the back edge of a table
  // there is no previous code, and inventing one would put a code in the agent's mouth.
  code: string | null;
  tone: 'used' | 'next';
  // How many codes the carril has been slid TOWARDS this slot. Absent on the slot that is not the
  // direction of travel, and on a carril sitting at the server pointer — the counter exists to say
  // «what you are reading is not the real siguiente», so at server state there is nothing to say.
  steps?: number;
}

// One fixed slot of a carril: a label over the code that currently occupies it. The slots never
// move — sliding the carril runs the codes THROUGH them — so this component knows nothing about
// offsets or sequences, only which code is standing here right now.
//
// The two tones are the whole reading of the block: `used` is the code already given (neutral
// fill, ink type — history), `next` is the one the agent is about to read out (brand fill, brand
// type, one step larger — the thing to act on). The distinction is never colour alone: the labels
// above them say it in words.
export function LockCodeSlot({ label, code, tone, steps }: LockCodeSlotProps) {
  const isNext = tone === 'next';
  return (
    <Box>
      {/* TODO: i18n — copy en ES hasta Fase 1. */}
      <Box sx={{ height: `${String(LOCK_CODE_LABEL_BLOCK_PX)}px` }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            height: `${String(LOCK_CODE_LABEL_LINE_PX)}px`,
          }}
        >
          <Text variant="overline" color="secondary" bold dense>
            {label}
          </Text>
          {steps === undefined ? null : (
            // Unsigned: the label it hangs off already carries the direction, so «+2» reads as two
            // codes ahead of the one the server would give next. Amber because it is the same
            // proposal the header's «PROPUESTA SIN GUARDAR» marks — one unsaved state, one colour.
            <Chip label={`+${String(steps)}`} color="warning" kind="counter" />
          )}
        </Box>
      </Box>
      <Box
        sx={{
          py: 1.5,
          px: 2,
          borderRadius: 2,
          backgroundColor: isNext ? citiboxColors.primarySurface : citiboxColors.surface,
        }}
      >
        {/* Codes are opening credentials: rendered as plain React text (default-escaped), never
            logged, never sent to analytics and never put in a URL. */}
        <Text variant={isNext ? 'h5' : 'h6'} color={isNext ? 'brand' : 'primary'} bold align="center">
          {code ?? '—'}
        </Text>
      </Box>
    </Box>
  );
}
